import type { HeroLibraryStore, ManagedHeroRecord } from "../schemas"
import { HeroLibraryStoreSchema } from "../schemas"

export const HERO_LIBRARY_STORAGE_KEY = "craft-force.hero-library"
export const HERO_LIBRARY_STORAGE_VERSION = 1
export const HeroLibraryStateSchema = HeroLibraryStoreSchema
export const EMPTY_HERO_LIBRARY: HeroLibraryStore = []

export const SEED_HERO_LIBRARY: HeroLibraryStore = [
  {
    hero: {
      heroId: "hero_ironclad",
      slug: "ironclad",
      name: "Ironclad",
      primaryColor: "red",
      progression: [
        {
          level: 1,
          targetCost: 0,
          card: {
            stats: { attack: 3, health: 5 },
            selectedSkills: [{ skillId: "guard" }],
            authoredCostInputs: {
              skillCostDelta: 1,
              nonExhaustSurcharge: 0,
              selfDestructSurcharge: 0,
              secondaryColorSurcharge: 0,
            },
            derivedCost: 5,
          },
        },
        {
          level: 2,
          targetCost: 3,
          card: {
            stats: { attack: 4, health: 6 },
            selectedSkills: [{ skillId: "guard" }, { skillId: "armor" }],
            authoredCostInputs: {
              skillCostDelta: 2,
              nonExhaustSurcharge: 0,
              selfDestructSurcharge: 0,
              secondaryColorSurcharge: 0,
            },
            derivedCost: 7,
          },
          deltaFromPrevious: {
            attackDelta: 1,
            healthDelta: 1,
            addedSkillIds: ["armor"],
          },
        },
        {
          level: 3,
          targetCost: 6,
          card: {
            stats: { attack: 5, health: 8 },
            selectedSkills: [
              { skillId: "guard" },
              { skillId: "armor" },
              { skillId: "rally" },
            ],
            authoredCostInputs: {
              skillCostDelta: 3,
              nonExhaustSurcharge: 0,
              selfDestructSurcharge: 0,
              secondaryColorSurcharge: 0,
            },
            derivedCost: 9,
          },
          deltaFromPrevious: {
            attackDelta: 1,
            healthDelta: 2,
            addedSkillIds: ["rally"],
          },
        },
      ],
    },
    updatedAt: "2026-03-20T12:00:00.000Z",
  },
  {
    hero: {
      heroId: "hero_sable",
      slug: "sable",
      name: "Sable",
      primaryColor: "blue",
      progression: [
        {
          level: 1,
          targetCost: 0,
          card: {
            stats: { attack: 2, health: 4 },
            selectedSkills: [{ skillId: "scout" }],
            authoredCostInputs: {
              skillCostDelta: 1,
              nonExhaustSurcharge: 0,
              selfDestructSurcharge: 0,
              secondaryColorSurcharge: 0,
            },
            derivedCost: 4,
          },
        },
        {
          level: 2,
          targetCost: 3,
          card: {
            stats: { attack: 3, health: 5 },
            selectedSkills: [{ skillId: "scout" }, { skillId: "shift" }],
            authoredCostInputs: {
              skillCostDelta: 2,
              nonExhaustSurcharge: 0,
              selfDestructSurcharge: 0,
              secondaryColorSurcharge: 0,
            },
            derivedCost: 6,
          },
          deltaFromPrevious: {
            attackDelta: 1,
            healthDelta: 1,
            addedSkillIds: ["shift"],
          },
        },
        {
          level: 3,
          targetCost: 6,
          card: {
            stats: { attack: 4, health: 6 },
            selectedSkills: [
              { skillId: "scout" },
              { skillId: "shift" },
              { skillId: "sneak" },
            ],
            authoredCostInputs: {
              skillCostDelta: 3,
              nonExhaustSurcharge: 0,
              selfDestructSurcharge: 0,
              secondaryColorSurcharge: 0,
            },
            derivedCost: 8,
          },
          deltaFromPrevious: {
            attackDelta: 1,
            healthDelta: 1,
            addedSkillIds: ["sneak"],
          },
        },
      ],
    },
    updatedAt: "2026-03-19T18:00:00.000Z",
  },
]

export function createHeroId(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, "_")
    .replace(/^_+|_+$/gu, "")
  return `hero_${slug || "custom"}_${Date.now().toString(36)}`
}

export function buildDuplicatedHeroRecord(
  source: ManagedHeroRecord,
  existingHeroes: HeroLibraryStore,
): ManagedHeroRecord {
  const existingNames = existingHeroes.map((r) => r.hero.name)
  const nextName = getNextDuplicateName(source.hero.name, existingNames)
  const newId = createHeroId(nextName)
  const slug = nextName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, "_")
    .replace(/^_+|_+$/gu, "")

  return {
    hero: { ...source.hero, heroId: newId, slug, name: nextName },
    ownerUserId: source.ownerUserId,
    remoteSyncFailedAt: undefined,
    remoteSyncedAt: undefined,
    updatedAt: new Date().toISOString(),
  }
}

function getNextDuplicateName(
  sourceName: string,
  existingNames: readonly string[],
): string {
  const lower = new Set(existingNames.map((n) => n.toLowerCase()))
  if (!lower.has(sourceName.toLowerCase())) return sourceName
  let i = 2
  while (lower.has(`${sourceName} ${i}`.toLowerCase())) i++
  return `${sourceName} ${i}`
}
